<template>
  <div class="rm-cal" :class="{ 'rm-cal--dense': dense }">
    <div class="rm-cal__nav">
      <button
        type="button"
        class="rm-cal__nav-btn"
        :aria-label="$t('report_mobile.prev_month')"
        @click="step(-1)"
      >
        <v-icon :size="dense ? 18 : 20" color="#1c1917">mdi-chevron-left</v-icon>
      </button>
      <span class="rm-cal__title">{{ title }}</span>
      <button
        type="button"
        class="rm-cal__nav-btn"
        :aria-label="$t('report_mobile.month_next')"
        @click="step(1)"
      >
        <v-icon :size="dense ? 18 : 20" color="#1c1917">mdi-chevron-right</v-icon>
      </button>
    </div>

    <div class="rm-cal__weekdays" aria-hidden="true">
      <span v-for="(weekday, index) in weekdays" :key="index">{{ weekday }}</span>
    </div>

    <div class="rm-cal__grid">
      <template v-for="(cell, index) in cells">
        <span v-if="!cell" :key="`blank-${index}`"></span>
        <span v-else :key="cell.iso" class="rm-cal__cell" :class="cell.cellClass">
          <button
            type="button"
            class="rm-cal__day rm-num"
            :class="cell.dayClass"
            :aria-label="cell.label"
            :aria-pressed="String(cell.selected)"
            @click="$emit('pick', cell.iso)"
          >
            {{ cell.day }}
          </button>
        </span>
      </template>
    </div>
  </div>
</template>

<script>
import moment from "moment";

const ISO = "YYYY-MM-DD";

/**
 * Mesečni kalendar za izbor perioda, za telefon i računar (ponedeljak je prvi dan). Ne drži izbor: dobija
 * start/end i javlja "pick" sa izabranim datumom; roditelj odlučuje šta je početak, a šta kraj.
 */
export default {
  name: "ReportCalendar",
  props: {
    /** Gušći raspored za padajući izbor na računaru */
    dense: {
      type: Boolean,
      default: false,
    },
    /** Početak izabranog perioda, "YYYY-MM-DD" */
    start: {
      type: String,
      default: "",
    },
    /** Kraj izabranog perioda; prazno dok se bira kraj */
    end: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      view: moment(this.start || undefined, ISO).startOf("month").format(ISO),
    };
  },
  computed: {
    locale() {
      const config = this.getCurrentLanguageConfig();
      return config.script === "latin" && config.locale === "sr-RS" ? "sr-Latn-RS" : config.locale;
    },
    monthStart() {
      return moment(this.view, ISO);
    },
    title() {
      const name = new Intl.DateTimeFormat(this.locale, { month: "long" }).format(
        this.monthStart.toDate()
      );
      return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${this.monthStart.year()}`;
    },
    weekdays() {
      const formatter = new Intl.DateTimeFormat(this.locale, { weekday: "narrow" });
      // 5. januar 2026. je ponedeljak
      return Array.from({ length: 7 }, (_, index) =>
        formatter.format(new Date(2026, 0, 5 + index)).toUpperCase()
      );
    },
    cells() {
      const offset = (this.monthStart.day() + 6) % 7;
      const days = this.monthStart.daysInMonth();
      const total = Math.ceil((offset + days) / 7) * 7;
      const today = moment().format(ISO);
      const range = Boolean(this.end) && this.end !== this.start;
      const formatter = new Intl.DateTimeFormat(this.locale, { dateStyle: "full" });
      return Array.from({ length: total }, (_, index) => {
        const day = index - offset + 1;
        if (day < 1 || day > days) {
          return null;
        }
        const date = this.monthStart.clone().date(day);
        const iso = date.format(ISO);
        const isStart = iso === this.start;
        const isEnd = Boolean(this.end) && iso === this.end;
        const inRange = range && iso > this.start && iso < this.end;
        return {
          iso,
          day,
          label: formatter.format(date.toDate()),
          selected: isStart || isEnd,
          cellClass: {
            "is-in-range": inRange,
            "is-range-start": range && isStart,
            "is-range-end": range && isEnd,
          },
          dayClass: {
            "is-edge": isStart || isEnd,
            "is-in-range": inRange,
            "is-today": iso === today && !isStart && !isEnd && !inRange,
          },
        };
      });
    },
  },
  watch: {
    /** Kad se period promeni spolja (brzi izbor), kalendar prelazi na mesec početka. */
    start(start) {
      if (start) {
        this.view = moment(start, ISO).startOf("month").format(ISO);
      }
    },
  },
  methods: {
    step(delta) {
      this.view = this.monthStart.clone().add(delta, "month").format(ISO);
    },
  },
};
</script>

<style lang="scss">
.rm-cal {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &__nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__nav-btn {
    width: 44px;
    height: 44px;
    border: none;
    background: transparent;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: pointer;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
  }

  &__weekdays,
  &__grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }

  &__weekdays {
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: var(--rm-muted);
  }

  &__grid {
    row-gap: 4px;
  }

  &__cell {
    display: block;

    &.is-in-range {
      background: var(--rm-accent-soft);
    }

    &.is-range-start {
      background: linear-gradient(90deg, transparent 50%, var(--rm-accent-soft) 50%);
    }

    &.is-range-end {
      background: linear-gradient(90deg, var(--rm-accent-soft) 50%, transparent 50%);
    }
  }

  &__day {
    width: 100%;
    height: 44px;
    border: none;
    border-radius: 12px;
    background: transparent;
    color: var(--rm-text);
    font-size: 15px;
    font-weight: 400;

    &.is-edge {
      background: var(--rm-accent);
      color: #ffffff;
      font-weight: 700;
    }

    &.is-in-range {
      color: var(--rm-accent-strong);
      font-weight: 600;
    }

    &.is-today {
      border: 1.5px solid var(--rm-text);
      font-weight: 700;
    }
  }

  &__day:focus-visible,
  &__nav-btn:focus-visible {
    outline: 2px solid var(--rm-accent);
    outline-offset: 2px;
  }

  &--dense &__nav-btn {
    width: 34px;
    height: 34px;
    border-radius: 10px;
  }

  &--dense &__nav-btn:hover,
  &--dense &__day:not(.is-edge):hover {
    background: var(--rm-chip-bg);
  }

  &--dense &__title {
    font-size: 15px;
  }

  &--dense &__weekdays {
    font-size: 11px;
  }

  &--dense &__day {
    height: 34px;
    border-radius: 10px;
    font-size: 13px;
  }
}
</style>
